import { FC, useCallback, useMemo, useState } from 'react';
import React from 'react';
import classNames from 'classnames';
import styles from './index.module.scss';
import { Form, Input, Button, Switch } from 'src/common';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { useUpdateCallOffsSearch, useCallOffsSearch } from 'src/models/callOffs/hooks';
import { INITIAL_CALL_OFFS_SEARCH } from 'src/models/callOffs/context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch } from '@fortawesome/pro-light-svg-icons';
import { CallOffsSubCategory, OverviewQp, ProcurementsDisplay } from 'src/models/procurements/types';
import useOverviewMode from 'src/reactiveVars/overviewModeVar';
import { useSetOverviewQp } from 'src/models/procurements/hooks';
import { EmailStatus } from 'src/models/callOffs/types';
import { useEffectOnce, useUnmount } from 'react-use';
import { splitByQuotation } from 'src/helpers';

interface IFormState {
  keywords: string;
  showHandled: boolean;
}
interface IProps {
  currentTab: CallOffsSubCategory;
}

export const CallOffsSearch: FC<IProps> = props => {
  const { currentTab } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const updateSearch = useUpdateCallOffsSearch();
  const searchVar = useCallOffsSearch();
  const procurementTabInfo = useOverviewMode();
  const setOverviewQp = useSetOverviewQp();

  const initialValues = useMemo(() => {
    return {
      keywords: (INITIAL_CALL_OFFS_SEARCH.keywords && INITIAL_CALL_OFFS_SEARCH.keywords.join(' ')) || '',
      showHandled: currentTab === CallOffsSubCategory.HANDLED
    };
  }, [currentTab]);

  useEffectOnce(() => {
    if (currentTab === CallOffsSubCategory.HANDLED) {
      updateSearch({ ...searchVar, statuses: [] });
    }
  });
  const onValuesChange = useMemo(
    () =>
      debounce((changedValues: Partial<IFormState>) => {
        const switcherWasTouched = changedValues.hasOwnProperty('showHandled');
        if (switcherWasTouched) {
          if (!!changedValues.showHandled && procurementTabInfo?.tab === ProcurementsDisplay.CALL_OFFS) {
            setOverviewQp({ [OverviewQp.SubCategory]: CallOffsSubCategory.HANDLED });
            updateSearch({ ...searchVar, statuses: [] });
          } else if (
            !changedValues.showHandled &&
            procurementTabInfo?.tab === ProcurementsDisplay.CALL_OFFS &&
            procurementTabInfo?.subCategory === CallOffsSubCategory.HANDLED
          ) {
            setOverviewQp({ [OverviewQp.SubCategory]: CallOffsSubCategory.NEW });
            updateSearch({ ...searchVar, statuses: [EmailStatus.UNHANDLED] });
          }
        } else {
          const filteredString = changedValues.keywords?.length ? splitByQuotation(changedValues.keywords) : [];
          updateSearch({ ...searchVar, keywords: (!!filteredString.length && filteredString) || undefined });
        }
      }, 300),
    [procurementTabInfo, searchVar, setOverviewQp, updateSearch]
  );

  const [searchFocus, setSearchFocus] = useState(false);
  const onFocus = useCallback(() => setSearchFocus(true), []);
  const onBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (!value) {
      setSearchFocus(false);
    }
  }, []);

  const onClearSearch = useCallback(() => {
    form.setFieldsValue({ keywords: '' });
    updateSearch({ ...searchVar, keywords: undefined });
  }, [form, searchVar, updateSearch]);

  const needClear = useMemo(() => {
    return (!!searchVar.keywords && !!searchVar.keywords.length) || false;
  }, [searchVar.keywords]);

  useUnmount(() => {
    onClearSearch();
  });

  return (
    <Form form={form} initialValues={initialValues} onValuesChange={onValuesChange} className={styles.form}>
      <div className={styles.search}>
        <Form.Item name={'keywords'} className={classNames(styles.searchField, { [styles.isFocus]: searchFocus })}>
          <Input
            placeholder={t('Common.search')}
            onBlur={onBlur}
            onFocus={onFocus}
            className={styles.searchInput}
            suffix={
              needClear ? (
                <Button
                  type={'text'}
                  onClick={onClearSearch}
                  className={styles.button}
                  icon={<FontAwesomeIcon icon={faTimes} />}
                />
              ) : (
                <FontAwesomeIcon icon={faSearch} className={styles.iconSearch} />
              )
            }
          />
        </Form.Item>
        <Form.Item
          name="showHandled"
          label={t('CallOffs.handled')}
          className={styles.switchField}
          valuePropName={'checked'}
        >
          <Switch className={styles.switch} />
        </Form.Item>
      </div>
    </Form>
  );
};
export default CallOffsSearch;
